import {
    baseImagesURL,
    getScene7ImageAndVideoUrl,
    getScene7ImageUrl,
} from '../constants';
import type {
    ProductAsset,
    ProductImage,
    ProductVideo,
    Scene7AssetData,
    Scene7ImageData,
    Scene7ImageItem,
    Scene7VideoData,
    Scene7VideoItemVideo,
} from '../types';
import { typedFetch } from './typedFetch';

// SB-308: Updated mainUrl to use webp format and smaller size to improve site performance
// TODO: change the size according to breakpoints
const buildImagesDto = (scene7Images: Scene7ImageItem[]): ProductImage[] =>
    scene7Images.map(currentImage => ({
        typeOfAsset: 'image',
        mainUrl: `${baseImagesURL}/is/image/${currentImage.i.n}?fmt=webp&wid=1400&hei=1400`,
        thumbnailUrl: `${baseImagesURL}/is/image/${currentImage.i.n}?fit=constrain,1&wid=80&hei=80`,
    }));

const buildVideosDto = (scene7Videos: Scene7VideoItemVideo['set']) => {
    const videosDto: ProductVideo[] = [];

    if (
        scene7Videos?.n?.includes('vid1') // Need to make sure 'n' property includes vid1
    ) {
        const videoDto: ProductVideo = {
            typeOfAsset: 'video',
            thumbnailUrl: `${baseImagesURL}/is/image/${scene7Videos.n}?fit=constrain,1&wid=80&hei=80&fmt=jpg`,
            posterImageUrl: `${baseImagesURL}/is/image/${scene7Videos.n}`,
            videoSizes: Array.isArray(scene7Videos?.item)
                ? scene7Videos.item.map(currentVideo => ({
                      url: `${baseImagesURL}/is/content/${currentVideo.i.n}`,
                  }))
                : [],
        };

        videosDto.push(videoDto);
    }

    return videosDto;
};

const handleAssetData = (hasVideo: boolean, assetsData: Scene7AssetData) => {
    const productAssets: ProductAsset[] = [];
    if (hasVideo) {
        const videosData = assetsData as Scene7VideoData;
        if (Array.isArray(videosData.set.item)) {
            productAssets.push(
                ...buildImagesDto(
                    Array.isArray(videosData.set.item[0].set.item)
                        ? videosData.set.item[0].set.item
                        : [videosData.set.item[0].set.item]
                )
            );
            if (videosData.set.item.length > 1 && videosData.set.item[1].set) {
                productAssets.push(
                    ...buildVideosDto(
                        (assetsData as Scene7VideoData).set.item[1].set
                    )
                );
            }
        }
    } else {
        const imagesData = assetsData as Scene7ImageData;

        productAssets.push(
            ...buildImagesDto(
                Array.isArray(imagesData.set.item)
                    ? imagesData.set.item
                    : [imagesData.set.item]
            )
        );
    }
    return productAssets;
};

export const getProductAssets = async (
    productId: string | number,
    productMasterId?: number,
    hasVideo = false
): Promise<ProductAsset[]> => {
    const imageUrl = getScene7ImageUrl(productId);
    const videoAndImageUrl = getScene7ImageAndVideoUrl(
        productId,
        productMasterId
    );

    let responseText =
        (await typedFetch<string>(hasVideo ? videoAndImageUrl : imageUrl)) ||
        '';

    // For some products, checkScene7VideoExists would return true, but getScene7ImageAndVideoUrl would return s7jsonError string.
    // We need errorRegex to handle that edgecase, and get the image set as fallback.
    const errorRegex = /s7jsonError/gi;
    const isResponseContainsError = errorRegex.test(responseText);

    if (hasVideo && isResponseContainsError) {
        responseText = (await typedFetch<string>(imageUrl)) || '';
        hasVideo = false;
    }

    const responseScene7DataRegex = /s7jsonResponse\((.*?),""\)/g;
    const match = responseScene7DataRegex.exec(responseText || '');

    if (!match?.[1]) {
        return [] as ProductAsset[];
    }

    const jsonString = match[1];
    let assetsData;
    try {
        assetsData = JSON.parse(jsonString) as Scene7AssetData;
    } catch (error) {
        console.error('Failed to parse Scene7 JSON response:', error);
        return [] as ProductAsset[];
    }

    return handleAssetData(hasVideo, assetsData);
};
